"use client";

import { useEffect, useState} from "react";
import ProductList from "@/components/ProductList";
import TopupFilters from "./TopupFilters";
import TopupFilterDrawer from "./TopupFilterDrawer"; // ✅ new drawer component
import PiBotLoader from "@/components/PiBotLoader.jsx";
import { enrichTopupOffer } from "../utils/enrichTopupOffer";
import { normalizeCountry, countryName } from "../utils/country-name";
import { fetchTopupOffers, fetchTopupCountries, fetchTopupRegions } from "@/utils/api/topup";


const loadingMessages = [
  "Loading top-up offers… Please Wait... 🚀",
  "Fetching the best deals for you… 💸",
];

export default function TopupSection({ brands, selectedProduct, onSelect }) {
  const [isFiltersLoading, setIsFiltersLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [allTopups, setAllTopups] = useState([]);
  const [msgIndex, setMsgIndex] = useState(0);
  const [filterOptions, setFilterOptions] = useState({
    brands: [],
    countries: [],
    regions: [],
    subTypes: [],
  });
const [allCountries, setAllCountries] = useState([]);
const [hamsterMode, setHamsterMode] = useState("normal");
  const [topupFilters, setTopupFilters] = useState({
    country: "",
    region: "",
    brand: "",
    subType: "",
  });

  const [page, setPage] = useState(1);
  const perPage = 20;
  const [isLoading, setIsLoading] = useState(true);
    const filtersLoading = isFiltersLoading || isLoading;
 

  // ✅ For mobile drawer
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);

  const handleResetFilters = () => {
    setTopupFilters({ country: "", region: "", brand: "", subType: "" });
    setPage(1);
  };

 const totalPages = Math.max(1, Math.ceil(total / perPage));

useEffect(() => {
  if (!isLoading) {
    setHamsterMode("normal");
    return;
  }
  setHamsterMode("normal");
  const t1 = setTimeout(() => setHamsterMode("overclock"), 4000);
  const t2 = setTimeout(() => setHamsterMode("meltdown"),  10000);
  return () => { clearTimeout(t1); clearTimeout(t2); };
}, [isLoading]);

useEffect(() => {
  let alive = true;

  (async () => {
    try {
      setIsFiltersLoading(true);

      const countryRows = await fetchTopupCountries();
      const countries = (countryRows || [])
        .map((c) => (c.code || c)?.toString().trim().toUpperCase())
        .filter(Boolean)
        .map((code) => ({ code, name: countryName(code) }))
        .sort((a, b) => a.name.localeCompare(b.name));

      const regions = ((await fetchTopupRegions()) || []).sort();

      if (!alive) return;

      // ✅ move these here (after computing)
      setAllCountries(countries);
      setFilterOptions((prev) => ({
        ...prev,
        countries,
        regions,
      }));
    } catch (e) {
      console.error("❌ Failed to load topup filter options:", e);
    } finally {
      if (alive) setIsFiltersLoading(false);
    }
  })();

  return () => {
    alive = false;
  };
}, []);


    // Rotate the loading message while we're loading
  useEffect(() => {
    if (!isLoading) return;
   const id = setInterval(() => {
     setMsgIndex((i) => (i + 1) % loadingMessages.length);
    }, 1600);
    return () => clearInterval(id);
 }, [isLoading]);

  // Load all offers once
 useEffect(() => {
  async function loadTopups() {
    try {
      setIsLoading(true);

      const { list: rawList, total: totalCount, facets } =
        await fetchTopupOffers({
          limit: perPage,
          offset: (page - 1) * perPage,
          ...topupFilters,
        });

      setTotal(totalCount || 0);

      const enriched = (rawList || [])
        .map((o) => {
          try {
            return {
              ...enrichTopupOffer(o, brands),
              type: "topup",
              country: normalizeCountry(o.country),
            };
          } catch {
            return null;
          }
        })
        .filter(Boolean);

      setAllTopups(enriched);

      // ✅ FACETS HANDLING (NO extra try/catch needed)
      if (facets) {
        setFilterOptions((prev) => {
          const next = {
            ...prev,
            brands: (facets.brands || []).sort(),
            regions: (facets.regions || []).sort(),
            subTypes: (facets.subTypes || []).sort(),
          };

          if (allCountries.length) {
            const codes = new Set(
              (facets.countries || []).map((x) =>
                String(x).toUpperCase()
              )
            );
            next.countries = allCountries.filter((c) =>
              codes.has(c.code)
            );
          }

          return next;
        });
      }
    } catch (err) {
      console.error("❌ Failed to load topups:", err);
    } finally {
      setIsLoading(false);
    }
  }

  loadTopups();
}, [brands, page, topupFilters, allCountries]);


  return (
    <div className="mt-4 grid gap-4 lg:gap-6 lg:grid-cols-[280px_1fr]">
      {/* 🔍 Floating Filter Button (Mobile Only) */}
      {!filterDrawerOpen && (
        <button
          onClick={() => setFilterDrawerOpen(true)}
          className="fixed bottom-4 right-4 sm:hidden z-50 bg-primary text-white px-4 py-3 rounded-full shadow-md inline-flex items-center gap-2 text-sm font-medium"
          aria-label="Open topup filters"
        >
          🔍 Filters
        </button>
      )}

      {/* 📱 Mobile Drawer */}
    <TopupFilterDrawer
  isOpen={filterDrawerOpen}
  onClose={() => setFilterDrawerOpen(false)}
  filters={topupFilters}
  setFilters={setTopupFilters}
  filterOptions={filterOptions}
  onReset={handleResetFilters}
  isLoading={filtersLoading}
/>


      {/* 💻 Desktop Sidebar Filters */}
    <aside className="hidden lg:block lg:sticky lg:top-16 h-max">
  <div className="bg-surface border border-border rounded-xl p-4">
    <div className="flex items-center justify-between mb-3">
      <h3 className="text-sm font-semibold text-textSecondary">Topup Filters</h3>
      <button
        onClick={handleResetFilters}
        disabled={filtersLoading}
        className="text-xs underline text-textSecondary hover:text-textPrimary disabled:opacity-60"
      >
        Reset
      </button>
    </div>

    {filtersLoading ? (
      <div className="flex flex-col items-center justify-center py-10">
        <PiBotLoader size={72} label="Loading filters…" />
        <p className="mt-2 text-xs text-textSecondary text-center">
          Preparing countries, regions & types…
        </p>
      </div>
    ) : (
      <TopupFilters
        filters={topupFilters}
        options={filterOptions}
        onChange={(key, value) => {
          setTopupFilters((prev) => {
            if (key === "country") {
              return { country: value, brand: "", region: "", subType: "" };
            }
            return { ...prev, [key]: value };
          });
          setPage(1);
        }}
      />
    )}
  </div>
</aside>


      {/* 🧾 Product List */}
      <section>
          {isLoading ? (
          <div className="flex flex-col items-center justify-center py-10">
            <PiBotLoader
              size={104}
              mode={hamsterMode}
              label={{
                normal:    "Fetching the best deals for you…",
                overclock: "Still working on it…",
                meltdown:  "This is taking a while, sorry 😅",
              }[hamsterMode]}
            />
            <p className="sr-only" aria-live="polite">Loading topup offers</p>
          </div>
        ) : allTopups.length === 0 ? (
          <div className="text-center text-textSecondary text-sm my-8">
            No topups found. Try adjusting your filters.
          </div>
        ) : (
          <ProductList
            products={allTopups}
            productType="TOPUP"
            selectedProduct={selectedProduct}
            onSelect={onSelect}
            page={page}
            totalPages={totalPages}
            onPageChange={setPage}
            variant="compact"
            columns="grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4"
          />
        )}
      </section>
    </div>
  );
}
